/**
 * CHECKOUT DRAFT SERVICE - Manages draft checkout sessions
 * Saves incomplete checkout data to Firestore so they can be resumed later
 */

import { 
  collection, 
  doc, 
  addDoc, 
  updateDoc, 
  getDocs,
  query,
  where,
  deleteDoc,
  serverTimestamp,
  onSnapshot
} from '../services/firestoreCompat';
import { db } from './firebaseConfig';

export interface CheckoutDraft {
  id?: string;
  draft_id: string; // Unique ID for this draft session
  cart_items: Array<{
    id: string;
    cartId: string;
    name: string;
    quantity: number;
    price: number;
    base_price?: number;
    tax_rate?: number;
    category_name?: string;
    image_url?: string;
  }>;
  customer_name?: string;
  customer_first_name?: string;
  customer_last_name?: string;
  customer_phone?: string;
  customer_address?: string;
  customer_postcode?: string;
  customer_city?: string;
  table_number?: number | null;
  order_type: 'dine_in' | 'takeaway' | 'delivery';
  payment_method: 'cash' | 'card' | 'paypal' | 'apple_pay' | 'google_pay';
  subtotal: number;
  tax: number;
  total: number;
  status: 'draft' | 'completed' | 'cancelled';
  source: 'restaurant' | 'dashboard' | 'unknown'; // Track where the order came from
  table_context?: {
    id?: string;
    number: number;
    type: string;
    area?: string;
    customer_name?: string;
    party_size?: number;
    start_time?: string;
  };
  created_by: string;
  created_at: string;
  updated_at: string;
  last_accessed_at?: string;
}

class CheckoutDraftService {
  private static instance: CheckoutDraftService;
  private readonly COLLECTION_NAME = 'checkout_drafts';

  private constructor() {
    console.log('💾 Checkout Draft Service initialized');
  }

  public static getInstance(): CheckoutDraftService {
    if (!CheckoutDraftService.instance) {
      CheckoutDraftService.instance = new CheckoutDraftService();
    }
    return CheckoutDraftService.instance;
  }

  /**
   * Generate a unique draft ID
   */
  private generateDraftId(): string {
    return `checkout_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  }

  /**
   * Save or update a draft checkout session to Firestore
   */
  public async saveDraft(
    draft: Omit<CheckoutDraft, 'id' | 'created_at' | 'updated_at' | 'draft_id'>,
    existingDraftId?: string
  ): Promise<string> {
    try {
      console.log('💾 [CHECKOUT DRAFT] Saving draft checkout to Firestore:', {
        table_number: draft.table_number,
        order_type: draft.order_type,
        status: draft.status,
        cart_items_count: draft.cart_items.length,
        total: draft.total,
        source: draft.source,
        existingDraftId
      });
      
      // Check if draft already exists (by table number if from restaurant, or by existingDraftId)
      let existingDraft: CheckoutDraft | null = null;
      
      if (existingDraftId) {
        // If draft_id is provided, check by it first
        existingDraft = await this.getDraftByDraftId(existingDraftId);
      } else if (draft.table_number && draft.source === 'restaurant') {
        // For restaurant orders, check by table number
        existingDraft = await this.getActiveDraftByTable(draft.table_number);
      }
      
      // Helper function to remove undefined values (Firestore doesn't allow undefined)
      const removeUndefined = (obj: any): any => {
        if (obj === null || obj === undefined) return null;
        if (Array.isArray(obj)) {
          return obj.map(item => removeUndefined(item));
        }
        if (typeof obj === 'object') {
          const cleaned: any = {};
          for (const key in obj) {
            if (obj[key] !== undefined) {
              cleaned[key] = removeUndefined(obj[key]);
            }
          }
          return cleaned;
        }
        return obj;
      };

      // Use existing draft_id or generate new one
      const draftId = existingDraft?.draft_id || this.generateDraftId();

      const draftData: any = removeUndefined({
        draft_id: draftId,
        cart_items: draft.cart_items,
        customer_name: draft.customer_name,
        customer_first_name: draft.customer_first_name,
        customer_last_name: draft.customer_last_name,
        customer_phone: draft.customer_phone,
        customer_address: draft.customer_address,
        customer_postcode: draft.customer_postcode,
        customer_city: draft.customer_city,
        table_number: draft.table_number,
        order_type: draft.order_type,
        payment_method: draft.payment_method,
        subtotal: draft.subtotal,
        tax: draft.tax,
        total: draft.total,
        status: draft.status,
        source: draft.source,
        table_context: draft.table_context,
        created_by: draft.created_by,
        updated_at: serverTimestamp(),
        last_accessed_at: serverTimestamp()
      });

      if (existingDraft && existingDraft.id) {
        // Update existing draft
        const draftRef = doc(db, this.COLLECTION_NAME, existingDraft.id);
        await updateDoc(draftRef, draftData);
        console.log('✅ [CHECKOUT DRAFT] Draft updated in Firestore:', {
          id: existingDraft.id,
          draft_id: draftId,
          table_number: draft.table_number,
          status: draft.status,
          source: draft.source
        });
        return existingDraft.id;
      } else {
        // Create new draft
        draftData.created_at = serverTimestamp();
        const docRef = await addDoc(collection(db, this.COLLECTION_NAME), draftData);
        console.log('✅ [CHECKOUT DRAFT] New draft saved to Firestore:', {
          id: docRef.id,
          draft_id: draftId,
          table_number: draft.table_number,
          status: draft.status,
          source: draft.source
        });
        return docRef.id;
      }
    } catch (error) {
      console.error('❌ [CHECKOUT DRAFT] Error saving draft to Firestore:', error);
      console.error('❌ [CHECKOUT DRAFT] Draft data that failed:', draft);
      throw error;
    }
  }

  /**
   * Get draft by draft ID
   */
  public async getDraftByDraftId(draftId: string): Promise<CheckoutDraft | null> {
    try {
      const q = query(
        collection(db, this.COLLECTION_NAME),
        where('draft_id', '==', draftId)
      );
      const querySnapshot = await getDocs(q);
      
      if (querySnapshot.empty) {
        return null;
      }
      
      const doc = querySnapshot.docs[0];
      return {
        id: doc.id,
        ...doc.data(),
        created_at: doc.data().created_at?.toDate?.()?.toISOString() || doc.data().created_at,
        updated_at: doc.data().updated_at?.toDate?.()?.toISOString() || doc.data().updated_at,
        last_accessed_at: doc.data().last_accessed_at?.toDate?.()?.toISOString() || doc.data().last_accessed_at
      } as CheckoutDraft;
    } catch (error) {
      console.error('❌ [CHECKOUT DRAFT] Error getting draft:', error);
      return null;
    }
  }

  /**
   * Get active draft by table number (for restaurant orders)
   */
  public async getActiveDraftByTable(tableNumber: number): Promise<CheckoutDraft | null> {
    try {
      const q = query(
        collection(db, this.COLLECTION_NAME),
        where('table_number', '==', tableNumber),
        where('status', '==', 'draft')
      );
      const querySnapshot = await getDocs(q);
      
      if (querySnapshot.empty) {
        return null;
      }
      
      // Return the most recent draft (by updated_at)
      const drafts = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        created_at: doc.data().created_at?.toDate?.()?.toISOString() || doc.data().created_at,
        updated_at: doc.data().updated_at?.toDate?.()?.toISOString() || doc.data().updated_at,
        last_accessed_at: doc.data().last_accessed_at?.toDate?.()?.toISOString() || doc.data().last_accessed_at
      })) as CheckoutDraft[];
      
      // Sort by updated_at descending and return the most recent
      drafts.sort((a, b) => {
        const aTime = new Date(a.updated_at || a.created_at || 0).getTime();
        const bTime = new Date(b.updated_at || b.created_at || 0).getTime();
        return bTime - aTime;
      });
      
      return drafts[0] || null;
    } catch (error) {
      console.error('❌ [CHECKOUT DRAFT] Error getting active draft by table:', error);
      return null;
    }
  }

  /**
   * Get all active drafts (draft status)
   */
  public async getActiveDrafts(): Promise<CheckoutDraft[]> {
    try {
      const q = query(
        collection(db, this.COLLECTION_NAME),
        where('status', '==', 'draft')
      );
      const querySnapshot = await getDocs(q);
      
      return querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        created_at: doc.data().created_at?.toDate?.()?.toISOString() || doc.data().created_at,
        updated_at: doc.data().updated_at?.toDate?.()?.toISOString() || doc.data().updated_at,
        last_accessed_at: doc.data().last_accessed_at?.toDate?.()?.toISOString() || doc.data().last_accessed_at
      })) as CheckoutDraft[];
    } catch (error) {
      console.error('❌ [CHECKOUT DRAFT] Error getting active drafts:', error);
      return [];
    }
  }

  /**
   * Mark draft as completed
   */
  public async completeDraft(draftId: string): Promise<void> {
    try {
      const draftRef = doc(db, this.COLLECTION_NAME, draftId);
      await updateDoc(draftRef, {
        status: 'completed',
        updated_at: serverTimestamp()
      });
      console.log('✅ [CHECKOUT DRAFT] Draft marked as completed:', draftId);
    } catch (error) {
      console.error('❌ [CHECKOUT DRAFT] Error completing draft:', error);
      throw error;
    }
  }

  /**
   * Cancel/delete a draft
   */
  public async cancelDraft(draftId: string): Promise<void> {
    try {
      const draftRef = doc(db, this.COLLECTION_NAME, draftId);
      await updateDoc(draftRef, {
        status: 'cancelled',
        updated_at: serverTimestamp()
      });
      console.log('✅ [CHECKOUT DRAFT] Draft cancelled:', draftId);
    } catch (error) {
      console.error('❌ [CHECKOUT DRAFT] Error cancelling draft:', error);
      throw error;
    }
  }

  /**
   * Delete a draft permanently
   */
  public async deleteDraft(draftId: string): Promise<void> {
    try {
      await deleteDoc(doc(db, this.COLLECTION_NAME, draftId));
      console.log('✅ [CHECKOUT DRAFT] Draft deleted:', draftId);
    } catch (error) {
      console.error('❌ [CHECKOUT DRAFT] Error deleting draft:', error);
      throw error;
    }
  }

  /**
   * Subscribe to active drafts (real-time updates)
   */
  public subscribeToActiveDrafts(callback: (drafts: CheckoutDraft[]) => void): () => void {
    const q = query(
      collection(db, this.COLLECTION_NAME),
      where('status', '==', 'draft')
    );
    
    return onSnapshot(q, (querySnapshot) => {
      const drafts = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        created_at: doc.data().created_at?.toDate?.()?.toISOString() || doc.data().created_at,
        updated_at: doc.data().updated_at?.toDate?.()?.toISOString() || doc.data().updated_at,
        last_accessed_at: doc.data().last_accessed_at?.toDate?.()?.toISOString() || doc.data().last_accessed_at
      })) as CheckoutDraft[];
      
      callback(drafts);
    });
  }
}

export default CheckoutDraftService;

